import { Box, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import { ApiWorkflowDialog } from '@verantos-dev/shared-components';

import { AvLibraryListWithSearch } from './AvLibraryListWithSearch';
import { useAddVariableFromLibraryModal } from './useAddVariableFromLibraryModal';

interface AddVariableFromLibraryModalProps {
  onClose: () => void;
}

export const AddVariableFromLibraryModal = ({ onClose }: AddVariableFromLibraryModalProps) => {
  const intl = useIntl();
  const {
    selectedAvIds,
    role,
    searchQuery,
    addError,
    isLoading,
    isError,
    filteredItems,
    inCohortAvIds,
    nameConflictAvIds,
    disabledReasons,
    roleOptions,
    canAdd,
    handleSelectAv,
    handleSelectAll,
    handleRoleChange,
    handleSearchChange,
    handleAdd,
    handleClose,
  } = useAddVariableFromLibraryModal(onClose);

  return (
    <ApiWorkflowDialog
      id="add-variable-from-library-modal"
      data-testid="add-variable-from-library-modal"
      title={intl.formatMessage({
        id: 'projects.analyticalVariables.library.addFromLibrary.modalTitle',
      })}
      onClose={handleClose}
      onSubmit={handleAdd}
      isValidForSubmit={canAdd}
      submittingError={addError ? { message: 'error' } : undefined}
      size="medium"
      dialogMessages={{
        entrySaveButtonText: intl.formatMessage(
          { id: 'projects.analyticalVariables.library.addFromLibrary.addVariables' },
          { count: selectedAvIds.size }
        ),
        entryCancelButtonText: intl.formatMessage({
          id: 'projects.analyticalVariables.library.cancel',
        }),
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Role in cohort — RadioGroup, bordered below */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="body1" fontWeight={500}>
            {intl.formatMessage({
              id: 'projects.analyticalVariables.library.addFromLibrary.roleLabel',
            })}
          </Typography>
          <RadioGroup
            value={role}
            onChange={(e) => handleRoleChange(e.target.value)}
            data-testid="add-variable-role-select"
          >
            {roleOptions.map((opt) => (
              <FormControlLabel
                key={opt.value}
                value={opt.value}
                control={<Radio color="primary" size="small" />}
                label={opt.label}
              />
            ))}
          </RadioGroup>
        </Box>

        {/* Select a variable — search + list */}
        <AvLibraryListWithSearch
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          items={filteredItems}
          isLoading={isLoading}
          isError={isError}
          multiple
          onSelect={handleSelectAv}
          onSelectAll={handleSelectAll}
          isSelected={(id) => selectedAvIds.has(id)}
          inCohortAvIds={inCohortAvIds}
          nameConflictAvIds={nameConflictAvIds}
          disabledReasons={disabledReasons}
          searchInputTestId="add-variable-search-input"
        />
      </Box>
    </ApiWorkflowDialog>
  );
};
